import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import Select from 'tio-reimagined/components/select';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type AccountModel from '../../models/account';
import type Owner from '@ember/owner';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
import type IntlService from 'ember-intl/services/intl';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type RouterService from '@ember/routing/router-service';

interface ChangeActiveAccountModalSignature {
  Args: {
    onClose: () => void;
    show: boolean;
    accountOptions: AccountModel[];
    accountReceivingContributions: AccountModel | null;
    personId: string | null;
    route: string;
    onAccountChanged: () => void;
  };
}

export default class ChangeActiveAccountModalComponent extends Component<ChangeActiveAccountModalSignature> {
  @service declare store: StoreService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked selectedAccount: AccountModel | null;

  constructor(owner: Owner, args: ChangeActiveAccountModalSignature['Args']) {
    super(owner, args);
    this.selectedAccount = this.args.accountReceivingContributions;
  }

  get selectedAccountId() {
    return this.selectedAccount?.id || '';
  }

  get isNewAccountSelected() {
    return this.args.accountReceivingContributions !== this.selectedAccount;
  }

  get isDisabled() {
    return !this.selectedAccount || !this.isNewAccountSelected;
  }

  get formatAccountOptions() {
    return this.args.accountOptions.map((account) => ({
      key: account.id,
      label: account.institutionName,
      description: account.number,
    }));
  }

  setSelectedAccount = (id: string | null) => {
    this.selectedAccount = id ? this.args.accountOptions.find((a) => a.id === id) || null : null;
  };

  updateAccountTask = dropTask(async () => {
    try {
      const orderedAccountIds = [
        this.selectedAccount!.id,
        ...this.args.accountOptions
          .filter((account) => account.id !== this.selectedAccount!.id)
          .map((account) => account.id),
      ];

      await this.store
        .adapterFor('person')
        // @ts-expect-error - Ember Data adapter method not typed on MinimumAdapterInterface
        .updateContributionAccount(this.args.personId, orderedAccountIds);

      this.router.refresh(this.args.route);
      this.notifications.add(this.intl.t('common.accounts.change_modal.success'), {
        appearance: 'success',
      });
      this.args.onAccountChanged();
      this.args.onClose();
    } catch (error) {
      console.error('Error updating contribution account:', error);
      this.notifications.add(this.intl.t('common.accounts.change_modal.error'), {
        appearance: 'error',
      });
    }
  });

  <template>
    <Modal @isOpen={{@show}} @onClose={{@onClose}} class="min-h-64" data-test-change-account-modal>
      <:header>{{t "common.accounts.change_modal.title"}}</:header>
      <:body>
        <Select
          data-test-account-select
          @clearable={{true}}
          @items={{this.formatAccountOptions}}
          @selected={{this.selectedAccountId}}
          @onChange={{this.setSelectedAccount}}
        />
        <div class="w-full mb-4 bg-gray-100 p-6 my-2" data-test-description>
          {{t "common.accounts.change_modal.change_account"}}
        </div>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.updateAccountTask.isRunning}}
          @onCancel={{@onClose}}
          @onSubmit={{this.updateAccountTask.perform}}
          @submitDisabled={{this.isDisabled}}
        />
      </:footer>
    </Modal>
  </template>
}
